import * as React from 'react'
import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import * as base from './api'

type PageProjectList = api.Pageable<api.ProjectList>

const USER_PROJECTS_CACHE = jotai.atom<hooks.Cache<PageProjectList>>({})

type UseUserProjectsConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseUserProjectsParams
  withToken?: boolean
}

export type UseUserProjectsParams = {
  limit?: number
  sort?: api.UserProjectSort
  state?: api.UserProjectState
  page?: number
}

export type UseUserProjects = {
  response: hooks.Response<PageProjectList>
  query: () => Promise<void>
}

export function toURLSearchParams({ limit, sort, page, state }: UseUserProjectsParams): URLSearchParams {
  const searchParams = new URLSearchParams()

  if (page !== undefined) {
    searchParams.set('offset', String((page - 1) * (limit ?? 20)))
  }

  searchParams.set('limit', limit !== undefined ? String(limit) : '10')

  if (state !== undefined) {
    searchParams.set('state', String(state))
  }
  if (sort !== undefined) {
    searchParams.set('sort', String(sort))
  }
  return searchParams
}

export function useUserProjects(
  userId: number,
  { fetchOnMount = true, params = {}, withToken = true }: UseUserProjectsConfig = {},
): UseUserProjects {
  const sharedQuery = hooks.useSharedQuery(
    async (userId: number, params: UseUserProjectsParams, withToken: boolean) =>
      base.list(userId, toURLSearchParams(params), withToken),
    {
      atom: USER_PROJECTS_CACHE,
      id,
    },
  )

  React.useEffect(() => {
    if (fetchOnMount === 'force' && getResponse().status !== hooks.QueryStatus.PENDING) {
      query()
    } else if (fetchOnMount && getResponse().status === hooks.QueryStatus.PRISTINE) {
      query()
    }
  }, [id(userId, params, withToken)])

  return {
    response: getResponse(),
    query,
  }

  function getResponse(): hooks.Response<PageProjectList> {
    if (userId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([userId, params, withToken])
  }

  function id(userId: number, params: UseUserProjectsParams, withToken: boolean): string {
    return `${String(userId)}:${JSON.stringify([params.limit, params.page, withToken])}`
  }

  async function query(): Promise<void> {
    if (userId === undefined) {
      return
    }

    return sharedQuery.query(userId, params, withToken)
  }
}
